import Button from "@common/components/Button";
import ListView from "@common/components/ListDetailViews/ListView";
import ItemListButton from "@common/components/ListDetailViews/ListView/ItemListButton";
import { useErrorNotification } from "@common/components/Notification";
import TopBanner from "@common/components/TopBanner";
import { WithGrow } from "@common/components/WithGrow";
import LinkWithQuery from "@pages/Assets/common/CountIndicator/LinkWithQuery";
import { getRoles } from "@services/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { AuthenticatedUser, Role as RoleType } from "@services/user/types";
import React, { useEffect, useMemo, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import Role from "../Role";
import MainBar from "./mainBar";
import { Box, Checkbox, Tooltip } from "@mui/material";
import ListTypography from "@common/components/ListDetailViews/ListView/ListTypography";
import { canEditRole, canViewUser } from "@common/utils/user";
import { useSpecificListPreferences } from "@common/components/SpecificPreferences/hooks";
import { attachPreferenceFunctions } from "@common/components/SpecificPreferences/utils";
import { SpecificPreferencesProvider } from "@common/components/SpecificPreferences";
import { ListViewPreferenceKey } from "@services/userPreferences/types";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";

type RolesAndCapabilitiesProps = {};

const getColumns = (
  t: TFunction,
  onRoleClick: (id: RoleType["id"]) => void,
  user?: AuthenticatedUser
): ColDef<RoleType>[] => [
  {
    key: "name",
    label: "Role",
    sortable: true,
    render: ({ id, name }) => (
      <ItemListButton
        label={name ?? ""}
        bookmark={false}
        onClick={() => onRoleClick && onRoleClick(id)}
        displayTicket={false}
        ticketColor="unavailable"
      />
    ),
  },
  {
    key: "description",
    label: "Description",
    sortable: true,
    render: ({ description }) => <ListTypography cellValue={description} />,
  },
  {
    key: "summary",
    label: "Users",
    width: 70,
    sortable: true,
    render: ({ id, name, summary }) => (
      <>
        {!canViewUser(user) ? (
          <Button disabled ignoreLocalization>
            {summary.totalUsers}
          </Button>
        ) : (
          <LinkWithQuery
            to={`/configuration/users?role.id=${id}`}
            state={{
              previous: t("Roles & Capabilities").toUpperCase(),
              current: name,
            }}
            keepSort={false}
          >
            <Button ignoreLocalization>{summary.totalUsers}</Button>
          </LinkWithQuery>
        )}
      </>
    ),
  },
];

const RolesAndCapabilities = (props: RolesAndCapabilitiesProps) => {
  const {} = props;
  const [user] = useAuthenticatedUser();
  const canEdit = canEditRole(user);
  const dispatchError = useErrorNotification();
  const [list, setList] = useState<RoleType[]>();
  const [sort, setSort] = useState<Sort<RoleType>>({
    column: "name",
    direction: "asc",
  });
  const [selected, setSelected] = useState<RoleType["id"][]>([]);
  const [clickedRole, setClickedRole] = useState<RoleType["id"]>();
  const { t } = useTranslation();
  const [{ columnWidths }, , { updateWidth }] = useSpecificListPreferences();
  const [includeAccounts, includeAll] = useUserSelectedAccounts();
  const fetch = async () => {
    setSelected([]);
    setList(undefined);
    try {
      setList(await getRoles(includeAll ? undefined : includeAccounts));
    } catch (e) {
      dispatchError(e);
    }
  };

  useEffect(() => {
    fetch();
  }, [includeAccounts]);

  const onRoleClose = async () => {
    setClickedRole(undefined);
    await fetch();
  };
  const sortList =
    ({ column, direction }: Sort<RoleType>) =>
    (a: RoleType, b: RoleType) => {
      if (column === "name" || column === "description") {
        return (direction === "asc" &&
          (a[column]?.toLowerCase() ?? "") >
            (b[column]?.toLowerCase() ?? "")) ||
          (direction === "desc" &&
            (a[column]?.toLowerCase() ?? "") < (b[column]?.toLowerCase() ?? ""))
          ? 1
          : -1;
      } else if (column === "summary") {
        return (direction === "asc" &&
          (a.summary.totalUsers ?? 0) > (b.summary.totalUsers ?? 0)) ||
          (direction === "desc" &&
            (a.summary.totalUsers ?? 0) < (b.summary.totalUsers ?? 0))
          ? 1
          : -1;
      }
      return 0;
    };

  const RowCheckbox = (props: SelectorBehaviorProps) => {
    const { t } = useTranslation();
    const { disabled } = props;

    return (
      <Tooltip
        title={
          disabled
            ? t(
                "This role cannot be deleted, it is assigned to some users or it is locked."
              )
            : ""
        }
        arrow
        placement="bottom-end"
      >
        <Box>
          <Checkbox {...props} disabled={disabled} />
        </Box>
      </Tooltip>
    );
  };

  const columns = useMemo(() => {
    const columns = getColumns(t, (id) => setClickedRole(id), user);
    return attachPreferenceFunctions(columns).applyColumnSizes(columnWidths);
  }, [t, user, columnWidths, list, sort]);

  return (
    <WithGrow
      fixed={<TopBanner title={t("Roles & Capabilities")} />}
      direction="column"
    >
      <WithGrow
        fixed={<MainBar selected={selected} fetch={fetch} canEdit={canEdit} />}
        direction="column"
      >
        <ListView
          data={list?.sort(sortList(sort)) ?? []}
          columns={columns}
          onColumnWidthChange={updateWidth}
          selectable
          customSelector={RowCheckbox}
          identityKey="id"
          isRowSelectable={(row) => !row.locked && row.summary.totalUsers === 0}
          onSelect={(selected) => setSelected(selected)}
          sort={sort}
          onSort={(column, direction) => setSort({ column, direction })}
          loading={list === undefined}
          disabledCheckbox={!canEdit}
          rememberScrollTop
        />
        <Role
          onClose={onRoleClose}
          open={clickedRole !== undefined}
          roleId={clickedRole}
        />
      </WithGrow>
    </WithGrow>
  );
};

const RolesAndCapabilitiesContainer = () => {
  return (
    <SpecificPreferencesProvider
      assetKey={ListViewPreferenceKey.ROLES_CAPABILITIES}
    >
      <RolesAndCapabilities />
    </SpecificPreferencesProvider>
  );
};

export default RolesAndCapabilitiesContainer;
